import React, { CSSProperties, useCallback } from "react";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./GetAppButton.scss";
import useVendorDetection from "../../hooks/useVendorDetection";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import {
  PhoneVendors,
  getVendorDownloadLink,
  getVendorLogo,
} from "../../utils/vendor";
import { openApp } from "../../utils/helpers";
import useGetApp from "../../hooks/useGetApp";
import { usePwaContext } from "../../context/pwa-context";
import useAnalytics from "../../hooks/useAnalytics";
import { DownloadAppOptionProperty } from "../../utils/analytics";

interface GetAppButtonProps {
  openDownloadAndroidApp: () => void;
  openTestFlightInstructions?: () => void;
  onBtnClick?: () => void;
  style?: CSSProperties;
}

const PHONE_VENDOR_STORE_NAMES = {
  [PhoneVendors.SAMSUNG]: "Galaxy Store",
  [PhoneVendors.AMAZON]: "Amazon Store",
  [PhoneVendors.TRANSSION]: "Palm Store",
  [PhoneVendors.APPLE]: "App Store",
};

export default function GetAppButton({
  openDownloadAndroidApp,
  style,
  onBtnClick,
}: GetAppButtonProps) {
  const { ctaText, appExists } = useGetApp();
  const phoneVendor = useVendorDetection();
  const { t } = useTranslation("common");
  const { userIsInPwa } = usePwaContext();
  const analytics = useAnalytics();

  const generateButtonText = useCallback(() => {
    const buttonText = t("use_or_get_app", {
      action: ctaText,
    });
    switch (phoneVendor) {
      case null:
        return buttonText;
      case PhoneVendors.APPLE:
        return (
          <span className="d-flex gap-2 align-items-center SpecialFontSettings">
            {buttonText}
            <span className="d-flex gap-1 align-items-center">
              <Image
                src={getVendorLogo(phoneVendor)}
                alt="Apple Logo"
                width={20}
                height={20}
              />
            </span>
          </span>
        );
      default:
        return (
          <span className="d-flex flex-column align-items-start text-white Euclid-500">
            <span className="SpecialFontSettings GetAppOn">
              {buttonText} on
            </span>
            <span className="SpecialFontSettings StoreName">
              {PHONE_VENDOR_STORE_NAMES[phoneVendor]}
            </span>
          </span>
        );
    }
  }, [t, ctaText, phoneVendor]);

  const downloadHyper = useCallback(() => {
    const storeLink = getVendorDownloadLink(phoneVendor);

    if (storeLink) {
      if (userIsInPwa) {
        window.location.href = storeLink;
        return;
      }
      window.open(storeLink as string, "_blank", "noopener,noreferrer");
    } else {
      openDownloadAndroidApp();
    }
  }, [openDownloadAndroidApp, phoneVendor, userIsInPwa]);

  const handleClick = useCallback(() => {
    if (appExists) openApp();
    else downloadHyper();
    onBtnClick?.();
    analytics.sendDownloadAppOptionsEvent(DownloadAppOptionProperty.GET_APP);
  }, [appExists, downloadHyper, onBtnClick, analytics]);

  return (
    <PrimaryActionButton
      text={generateButtonText()}
      onClick={handleClick}
      leftImage={
        phoneVendor !== PhoneVendors.APPLE
          ? getVendorLogo(phoneVendor)
          : undefined
      }
      imgClassName={`${
        phoneVendor === null ? "AndroidIcon" : "OtherStoreIcons"
      }`}
      buttonClassName="GetAppButton"
      style={style}
    />
  );
}
